import { message, Upload } from "antd";
import { useState } from "react";
import { UploadedDocument } from "../../../types/document";
import { uploadDocumentMedia } from "../../../utils/media";
import { antdBeforeUploadAllowList } from "../../../utils/uploadValidation";

type UploadDocumentOptions = {
  maxSize: number; // in MB
  allowedTypes: string[];
  acceptExtensions: string;
  disabled?: boolean;
  onSuccess: (document: UploadedDocument) => void;
};

export const useUploadDocument = (options: UploadDocumentOptions) => {
  const { maxSize, allowedTypes, acceptExtensions, disabled = false, onSuccess } = options;

  const [isFileUploading, setIsFileUploading] = useState(false);
  const [attachmentsFile, setAttachmentsFile] = useState([]);

  const MAX_FILE_SIZE = maxSize * 1024 * 1024;

  const uploadFile = async (file: File) => {
    try {
      setIsFileUploading(true);
      const response = await uploadDocumentMedia({ file_asset: file });
      const uploadedDocument = response?.data?.result as UploadedDocument;
      if (uploadedDocument) {
        setAttachmentsFile([file as any]);
        onSuccess(uploadedDocument);
        message.success("File uploaded successfully");
        return uploadedDocument;
      }
    } catch (error) {
      message.error("Upload failed. Please try again.");
      console.error(error);
      return null;
    } finally {
      setIsFileUploading(false);
    }
  };

  const clearFiles = () => {
    setAttachmentsFile([]);
  };

  const uploadProps = {
    className: "file-upload",
    name: "file",
    multiple: false,
    showUploadList: false,
    accept: acceptExtensions,
    disabled: disabled || isFileUploading,
    async onChange(info: any) {
      const file = info.fileList[0]?.originFileObj;
      if (file) {
        await uploadFile(file);
      }
    },
    beforeUpload: async (file: File) => {
      const allowedExtensions = acceptExtensions
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return await antdBeforeUploadAllowList(
        file,
        {
          maxBytes: MAX_FILE_SIZE,
          allowedMimeTypes: allowedTypes,
          allowedExtensions,
          label: acceptExtensions.toUpperCase(),
        },
        (msg) => message.error(msg)
      );
    },
    attachmentsFile,
  };

  return {
    isFileUploading,
    uploadProps,
    clearFiles,
  };
};
